import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { parse, serialize } from "cookie"
import { Config } from "./config"
import type { Environment } from "./config"

// The session is shared with new Manage app (v3) through cookies on `.figurepos.com`, so both apps read
// and write the same names with the same scope. Development uses a separate namespace because
// app-dev.figurepos.com and app.figurepos.com otherwise receive the same parent-domain cookies.
const SHARED_COOKIE_DOMAIN = ".figurepos.com"

export type ManageCookieName =
    | "session"
    | "user-id"
    | "user-email"
    | "user-role"
    | "account-id"
    | "organization-id"
    | "location-id"
    | "override-location-id"
    | "location-filter-ids"
    | "terminal-ids"
    | "sidebar-collapsed"

type CookieEnvironment = "dev" | "prod"
export type StorageCookieName = `manage-${CookieEnvironment}-${ManageCookieName}`

export const getCookieName = (
    name: ManageCookieName,
    environment: Environment = Config.environment
): StorageCookieName => {
    const cookieEnvironment: CookieEnvironment = environment === "production" ? "prod" : "dev"
    return `manage-${cookieEnvironment}-${name}`
}

export const cookieNames = {
    session: getCookieName("session"),
    userId: getCookieName("user-id"),
    userEmail: getCookieName("user-email"),
    userRole: getCookieName("user-role"),
    accountId: getCookieName("account-id"),
    organizationId: getCookieName("organization-id"),
    locationId: getCookieName("location-id"),
    overrideLocationId: getCookieName("override-location-id"),
    locationFilterIds: getCookieName("location-filter-ids"),
    terminalIds: getCookieName("terminal-ids"),
} as const

export const sidebarCollapsedCookie = getCookieName("sidebar-collapsed")

const getCookieDomain = (): Maybe<string> => {
    if (typeof window === "undefined") {
        return null
    }

    return window.location.hostname.endsWith(SHARED_COOKIE_DOMAIN) ? SHARED_COOKIE_DOMAIN : null
}

const expireHostOnly = (name: string) => {
    document.cookie = serialize(name, "", {
        maxAge: -1,
        path: "/",
    })
}

export const setCookie = (name: string, value: string, exp: number = 1) => {
    if (typeof document === "undefined") {
        return
    }

    const domain = getCookieDomain()

    if (domain) {
        expireHostOnly(name)
    }

    document.cookie = serialize(name, value, {
        maxAge: exp * 24 * 60 * 60,
        path: "/",
        sameSite: "lax",
        ...(domain ? { domain: domain, secure: true } : null),
    })
}

export const deleteCookie = (name: string) => {
    if (typeof document === "undefined") {
        return
    }

    const domain = getCookieDomain()

    // Expire the cookie on the current domain so that it's not accessible from other subdomains.
    expireHostOnly(name)

    if (domain) {
        document.cookie = serialize(name, "", {
            maxAge: -1,
            path: "/",
            domain: domain,
        })
    }
}

export const getCookie = (name: string, serverReq?: Maybe<Record<string, any>>): Maybe<string> => {
    const cookie = getCookieObject(serverReq)

    if (!cookie || !cookie[name]) {
        return null
    }

    return cookie[name]
}

const getCookieObject = (serverReq: Maybe<Record<string, any>>): Maybe<Record<string, string>> => {
    if (serverReq && serverReq.headers) {
        return parse(serverReq.headers.cookie ? serverReq.headers.cookie : "")
    } else if (typeof document !== "undefined") {
        return parse(document.cookie)
    } else {
        return null
    }
}
